import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { useRouteMasterStore } from "./RouteMasterStore/useRouteMasterStore";
import RouteModalCloseButton, {
  routeModalHeaderClassName,
} from "./RouteModalCloseButton";
import RouteStoppageMapModalHost from "./RouteStoppageMapModalHost";
import RouteStoppagePointsSection from "./RouteStoppagePointsSection";
import { useRouteVehicleModalData } from "./useRouteVehicleModalData";
import { getVehicleNumber } from "./routeVehicleModalUtils";

const AssignVehicleModal = ({ showModal, setShowModal, editRow }) => {
  const { isSaving, assignVehicleToRoute } = useRouteMasterStore();
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [reason, setReason] = useState("");

  const {
    vehicles,
    stoppages,
    routeDetail,
    isLoadingRoute,
    activeStoppageIndex,
    setActiveStoppageIndex,
    routePoints,
    openStoppageMap,
    handleAddStoppage,
    handleCloseStoppageMap,
    handleDeleteStoppage,
    handleSaveStoppage,
    getRouteMeta,
  } = useRouteVehicleModalData({ showModal, editRow });

  useEffect(() => {
    if (!showModal) return;
    setSelectedVehicleId("");
    setReason("");
  }, [showModal, editRow?.routeId]);

  const handleClose = () => {
    setSelectedVehicleId("");
    setReason("");
    setShowModal(false);
  };

  const handleProceed = async () => {
    if (!selectedVehicleId) {
      toast.error("Please select a vehicle to assign.");
      return;
    }

    const selectedVehicle = vehicles.find(
      (vehicle) => String(vehicle.vehicleId) === String(selectedVehicleId)
    );

    const result = await assignVehicleToRoute({
      ...getRouteMeta(),
      assignedVehicleId: selectedVehicleId,
      assignedVehicleNumber: getVehicleNumber(selectedVehicle),
      reason,
      stoppages,
      routeCoordinates: routePoints,
    });

    if (result?.success) {
      handleClose();
    }
  };

  return (
    <>
      <Modal
        size="xl"
        show={showModal && activeStoppageIndex === null}
        onHide={handleClose}
        backdrop="static"
        centered
        contentClassName="overflow-visible"
        dialogClassName="overflow-visible"
        aria-labelledby="assign-vehicle-modal-title"
      >
        <Modal.Header className={routeModalHeaderClassName}>
          <Modal.Title id="assign-vehicle-modal-title">Assign Vehicle</Modal.Title>
          <RouteModalCloseButton onClick={handleClose} />
        </Modal.Header>
        <hr className="mt-4 mb-0" />

        <Modal.Body
          className="pt-3"
          style={{ maxHeight: "calc(100vh - 8rem)", overflowY: "auto" }}
        >
          <div className="row">
            <div className="col-md-4 mb-3">
              <label htmlFor="assignRouteCode" className="form-label">
                Route Code
              </label>
              <input
                id="assignRouteCode"
                type="text"
                className="form-control"
                value={routeDetail?.routeCode ?? editRow?.routeCode ?? ""}
                readOnly
              />
            </div>

            <div className="col-md-8 mb-3">
              <label htmlFor="assignRouteDescription" className="form-label">
                Route Description
              </label>
              <textarea
                id="assignRouteDescription"
                className="form-control"
                rows={3}
                value={routeDetail?.routeDescription ?? editRow?.routeDescription ?? ""}
                readOnly
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="assignVehicleSelect" className="form-label">
                Assign Vehicle
              </label>
              <select
                id="assignVehicleSelect"
                className="form-select"
                value={selectedVehicleId}
                onChange={(event) => setSelectedVehicleId(event.target.value)}
              >
                <option value="">Select Vehicle Number</option>
                {vehicles.map((vehicle) => (
                  <option key={vehicle.vehicleId} value={vehicle.vehicleId}>
                    {vehicle.otherNo || getVehicleNumber(vehicle)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="assignReason" className="form-label">
                Reason
              </label>
              <input
                id="assignReason"
                type="text"
                className="form-control"
                placeholder="Enter reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
          </div>

          <RouteStoppagePointsSection
            stoppages={stoppages}
            isLoadingRoute={isLoadingRoute}
            onEditStoppage={openStoppageMap}
            onDeleteStoppage={handleDeleteStoppage}
            onAddStoppage={handleAddStoppage}
          />

          <hr className="mt-2 mb-3" />
          <div className="d-flex justify-content-end gap-2 pb-1">
            <button
              type="button"
              className="btn btn-outline-primary px-4"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary px-4"
              onClick={handleProceed}
              disabled={isSaving || isLoadingRoute}
            >
              {isSaving ? "Saving..." : "Proceed"}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <RouteStoppageMapModalHost
        showModal={showModal}
        activeStoppageIndex={activeStoppageIndex}
        setActiveStoppageIndex={setActiveStoppageIndex}
        onCloseStoppageMap={handleCloseStoppageMap}
        routeId={editRow?.routeId || 0}
        routePoints={routePoints}
        stoppages={stoppages}
        onSaveStoppage={handleSaveStoppage}
      />
    </>
  );
};

export default AssignVehicleModal;
